import { graphqlClient } from '@/lib/graphql-client';
import { useAuthStore } from '@/state/store/auth';

/**
 * Cross-cutting audit-logging helper.
 *
 * Writes one row to the `ActivityLog` schema (already live in Blocks OS) via
 * `insertActivityLog`. Any module can call this after its own mutation
 * succeeds to record "who did what" for the hr-logs viewer
 * (`src/modules/hr-logs`).
 *
 * Deliberately NOT a React hook -- mutation flows call it from a plain
 * `onSuccess` callback / async function, not render. The current user is
 * resolved the same way the rest of the app does outside of components (see
 * `src/lib/auth/org-access.ts`): `useAuthStore.getState()`.
 *
 * This must never throw or reject in a way that breaks the caller's own
 * mutation flow -- audit logging is best-effort. Any failure is swallowed
 * and only surfaced via `console.warn`.
 */

type ActivityLogAction = 'Insert' | 'Update' | 'Delete';

export interface LogActivityEntry {
  /** e.g. 'hr-department-manager' -- the feature module recording the event. */
  moduleName: string;
  /** e.g. 'Department' -- the affected schema's name. */
  entityType: string;
  /** The affected record's ItemId. */
  entityId: string;
  action: ActivityLogAction;
  /** Human-readable one-liner, e.g. `Created department "Engineering"`. */
  summary: string;
  /** Optional structured detail, JSON.stringify'd into ChangesJson. */
  changes?: Record<string, unknown>;
}

const INSERT_ACTIVITY_LOG_MUTATION = `
  mutation InsertActivityLog($input: ActivityLogInsertInput!) {
    insertActivityLog(input: $input) {
      itemId
      totalImpactedData
      acknowledged
    }
  }
`;

const resolveActor = (): { performedBy: string; performedByName: string } => {
  const user = useAuthStore.getState().user;
  if (!user) return { performedBy: '', performedByName: '' };

  return {
    performedBy: user.itemId ?? '',
    performedByName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
  };
};

export async function logActivity(entry: LogActivityEntry): Promise<void> {
  try {
    const { performedBy, performedByName } = resolveActor();

    await graphqlClient.mutate({
      query: INSERT_ACTIVITY_LOG_MUTATION,
      variables: {
        input: {
          ModuleName: entry.moduleName,
          EntityType: entry.entityType,
          EntityId: entry.entityId,
          Action: entry.action,
          PerformedBy: performedBy,
          PerformedByName: performedByName,
          Summary: entry.summary,
          ChangesJson: entry.changes ? JSON.stringify(entry.changes) : '',
        },
      },
    });
  } catch (error) {
    // Audit logging must never block or fail the caller's real business
    // action -- log and move on.
    console.warn('[activity-log] failed to record activity log entry', error);
  }
}
